import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, CircularProgress, Alert,
  Container, Paper, TextField, IconButton
} from '@mui/material';
import { CheckCircle, Cancel, Close } from '@mui/icons-material';
import api from '../../services/api';
import { REFERRAL_UPDATE_INVITE_STATUS, REFERRAL_VALIDATE_INVITE, REFERRAL_RECORD_INVITE_VISIT, CHECK_USER_EXISTS } from '../../config/Config';

const InviteLanding = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [checkingUser, setCheckingUser] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const [showingError, setShowingError] = useState(false);
  const [redirectingToLogin, setRedirectingToLogin] = useState(false);

  useEffect(() => {
    // Token is already extracted by the route parameter
    if (token && token.trim()) {
      loadInvite(token);
    } else {
      setError('Invalid invite link. No token provided.');
      setLoading(false);
                           // Redirect to home page with error message
        setTimeout(() => {
          navigate('/', { 
            state: { 
              error: 'Invalid or expired invite token. Please contact the person who sent you this invitation.' 
            } 
          });
        }, 2000);
    }
  }, [token, navigate]);

  // Debug: Monitor state changes
  useEffect(() => {
    console.log('State changed:', { error, showingError, userStatus, redirectingToLogin });
  }, [error, showingError, userStatus, redirectingToLogin]);

     // Handle redirect to login when redirectingToLogin becomes true
   useEffect(() => {
     if (redirectingToLogin) {
       console.log('redirectingToLogin is true, navigating to login');
       navigate('/login');
     }
   }, [redirectingToLogin, navigate]);

   // Cleanup: Clear referral code cookie when component unmounts
   useEffect(() => {
     return () => {
       // Only clear referral code cookie if we're not proceeding to pricing
       // Check if we have a pending navigation to pricing
       const hasInviteReferralCode = sessionStorage.getItem('inviteReferralCode');
       if (!hasInviteReferralCode) {
         document.cookie = 'ref_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
         console.log('Referral code cookie cleared on component unmount');
       } else {
         console.log('Keeping referral code cookie - user is proceeding to pricing');
       }
     };
   }, []);





  const loadInvite = async (inviteToken) => {
    try {
      setLoading(true);
      
      // Validate token format (basic check)
      if (!inviteToken || inviteToken.length < 10) {
        setError('Invalid invite token format');
        setLoading(false);
        return;
      }
      
             // Call backend to validate token and get invite details
       const response = await api.get(`${REFERRAL_VALIDATE_INVITE}${inviteToken}`);
      
                           if (response.data.valid) {
          setInvite(response.data.invite);
          // Record this click/visit (fire-and-forget; backend uses IP for unique count)
          api.get(REFERRAL_RECORD_INVITE_VISIT(inviteToken)).catch(() => {});
          // Don't set the ref_code cookie here - only set it when user actually proceeds
          setLoading(false);
                 } else {
           // Clear referral code cookie for invalid token
           document.cookie = 'ref_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
           console.log('Referral code cookie cleared - invalid token');
           const msg = (response.data.error === 'Link expired')
             ? 'Link expired'
             : 'Invalid or expired invite token. Please contact the person who sent you this invitation.';
           setError(msg);
           setShowingError(true);
           setTimeout(() => {
             navigate('/', { state: { error: msg } });
           }, 3000);
           setLoading(false);
         }
         } catch (err) {
       console.error('Error loading invite:', err);
       document.cookie = 'ref_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
       const msg = err.response?.data?.error === 'Link expired'
         ? 'Link expired'
         : 'Invalid or expired invite token. Please contact the person who sent you this invitation.';
       setError(msg);
       setShowingError(true);
       setTimeout(() => { navigate('/', { state: { error: msg } }); }, 3000);
       setLoading(false);
     }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    // Clear previous user status and error states when email changes
    if (userStatus) {
      setUserStatus(null);
    }
         if (showingError) {
       setShowingError(false);
       setError(null);
     }
     if (redirectingToLogin) {
       setRedirectingToLogin(false);
     }
    if (emailError) {
      validateEmail(value);
    }
  };

           const checkExistingUser = async (email) => {
      console.log('=== checkExistingUser START ===');
      console.log('Checking email:', email);
      
      try {
        setCheckingUser(true);
        console.log('Making API call to /accounts/check-user-exists/');
                 const response = await api.post(CHECK_USER_EXISTS, { email });
         console.log('API response received:', response.data);
         console.log('Response details:', {
           exists: response.data.exists,
           has_active_subscription: response.data.has_active_subscription,
           user_id: response.data.user_id,
           email: response.data.email
         });
         console.log('Backend response type check:', {
           exists_type: typeof response.data.exists,
           has_active_subscription_type: typeof response.data.has_active_subscription,
           exists_value: response.data.exists,
           has_active_subscription_value: response.data.has_active_subscription
         });
        
                                   if (response.data.exists && response.data.has_active_subscription) {
            console.log('User exists and account is active');
            return {
              exists: true,
              hasActiveSubscription: true,
              message: 'An account with this email already exists and is active. Please login to access your account.'
            };
                     } else if (response.data.exists && !response.data.has_active_subscription) {
             console.log('User exists but account is inactive');
             console.log('Decision: INACTIVE USER - ALLOW to proceed to pricing');
             return {
               exists: true,
               hasActiveSubscription: false,
               message: 'An account with this email already exists but is inactive. You can proceed with registration to reactivate your account.'
             };
           } else {
             console.log('User does not exist');
             console.log('Decision: NEW USER - ALLOW to proceed to pricing');
             return {
               exists: false,
               hasActiveSubscription: false,
               message: 'This email is not registered. You can proceed with registration to create a new account.'
             };
           }
      } catch (error) {
        console.error('Error checking user:', error);
        return {
          exists: false,
          hasActiveSubscription: false,
          message: 'Unable to verify user status. Please try again.'
        };
      } finally {
        setCheckingUser(false);
        console.log('=== checkExistingUser END ===');
      }
    };

          const handleAccept = async () => {
      console.log('=== handleAccept START ===');
      /*
       * FLOW LOGIC:
       * 1. ACTIVE USER (exists=true, has_active_subscription=true) → Redirect to login
       * 2. INACTIVE USER (exists=true, has_active_subscription=false) → Proceed to pricing
       * 3. NEW USER (exists=false, has_active_subscription=false) → Proceed to pricing
       */
      if (!validateEmail(email)) {
        console.log('Email validation failed');
        return;
      }

     console.log('Email validation passed, checking user existence...');
     const status = await checkExistingUser(email);
     console.log('User check result:', status);
     
     // Set user status first to update the UI
     setUserStatus(status);
     console.log('User status set in state');
     
     // Add a small delay to ensure the UI updates before any navigation
     await new Promise(resolve => setTimeout(resolve, 200));
     console.log('After 200ms delay');
     
            if (status.hasActiveSubscription) {
         console.log('=== ACTIVE USER ACCOUNT DETECTED ===');
         console.log('Setting error for active user account');
         
         // Clear any existing referral code cookie since user cannot proceed
         document.cookie = 'ref_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
         console.log('Referral code cookie cleared for active user');
         
         // Set error message and redirect after delay (same pattern as invalid token)
         const errorMessage = 'An account with this email already exists and is active. Please login to access your account.';
       
       // Clear any previous user status to ensure error takes precedence
       setUserStatus(null);
       console.log('User status cleared');
       
       // Set the error message and showing error state
       setError(errorMessage);
       setShowingError(true);
       
       console.log('Error state set:', errorMessage);
       console.log('showingError set to true');
       
       // Set redirecting state to true after 3 seconds
       setTimeout(() => {
         console.log('Setting redirectingToLogin to true');
         setRedirectingToLogin(true);
       }, 3000);
       
       console.log('=== RETURNING EARLY - should show error message ===');
       return; // Stop here, don't continue with registration flow
     }
     
                  console.log('=== NO ACTIVE USER ACCOUNT - proceeding to pricing ===');
      console.log('User status details:', {
        exists: status.exists,
        hasActiveSubscription: status.hasActiveSubscription,
        message: status.message
      });
      
      // Determine user type for logging
      if (status.exists && !status.hasActiveSubscription) {
        console.log('User type: EXISTING INACTIVE USER - can proceed to pricing');
      } else if (!status.exists) {
        console.log('User type: NEW USER - can proceed to pricing');
      }
      
      // User can proceed with registration (either new user or existing without active subscription)
      
      // Set the referral code cookie ONLY when user can proceed
      if (invite && invite.code_details && invite.code_details.code) {
        // Set cookie with longer expiration and mark it as from invite
        document.cookie = `ref_code=${invite.code_details.code}; path=/; max-age=${30*24*60*60}`;
        // Also store in sessionStorage as backup
        sessionStorage.setItem('inviteReferralCode', invite.code_details.code);
        console.log('Referral code cookie set for proceeding user:', invite.code_details.code);
      } else {
        console.log('No invite code details found, cannot set referral cookie');
      }
      
      // Update invite status to 'ACCEPTED'
      try {
        await api.post(`${REFERRAL_UPDATE_INVITE_STATUS}${token}/`, {
          action: 'accept',
          email: email
        });
        console.log('Invite status updated to ACCEPTED');
      } catch (error) {
        console.error('Failed to update invite status:', error);
        // Continue with the flow even if status update fails
      }
      
      // Store email in session storage for registration
      sessionStorage.setItem('inviteEmail', email);
      console.log('Email stored in session storage');
      
      // Navigate to pricing page - now with the referral code properly set
      console.log('Navigating to pricing page...');
      navigate('/get-started');
      console.log('=== handleAccept END ===');
   };

     const handleDecline = async () => {
       // Update invite status to 'REJECTED'
       try {
         await api.post(`${REFERRAL_UPDATE_INVITE_STATUS}${token}/`, {
           action: 'reject',
           email: email
         });
         console.log('Invite status updated to REJECTED');
       } catch (error) {
         console.error('Failed to update invite status:', error);
         // Continue with the flow even if status update fails
       }
       
       // Clear referral code cookie when user declines
       document.cookie = 'ref_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
       console.log('Referral code cookie cleared - user declined');
       // Navigate to home page
       navigate('/');
   };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && showingError) {
    console.log('=== RENDERING ERROR STATE ===');
    console.log('Error:', error);
    console.log('showingError:', showingError);
    return (
      <Container maxWidth="sm">
        <Box mt={4}>
          <Alert severity="error">
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {error}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Redirecting in 3 seconds...
              </Typography>
            </Box>
          </Alert>
        </Box>
      </Container>
    );
  }

         console.log('=== RENDERING MAIN INVITE FORM ===');
     console.log('Current state:', { error, showingError, userStatus, redirectingToLogin });
     return (
     <Container maxWidth="sm">
       <Box mt={4}>
         <Card>
           <CardContent>
                          <Box position="relative">
                {/* Close button */}
                <IconButton
                  onClick={() => navigate('/')}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: 'text.secondary'
                  }}
                >
                  <Close />
                </IconButton>
               
               <Box textAlign="center" py={4}>
                 <CheckCircle color="primary" sx={{ fontSize: 64, mb: 2 }} />
                 <Typography variant="h4" gutterBottom>
                   You've Been Invited!
                 </Typography>
                 <Typography variant="body1" color="text.secondary" paragraph>
                   {invite ? `You have been invited by ${invite.invited_by_email} to join our platform.` : 'You have been invited to join our platform.'} When you proceed, you'll be able to select a plan with special pricing. Would you like to continue?
                 </Typography>
                 
                 
                 
                 {/* Email input field */}
                 <Box mt={3} mb={4}>
                   <TextField
                     fullWidth
                     label="Email Address"
                     type="email"
                     value={email}
                     onChange={handleEmailChange}
                     error={!!emailError}
                     helperText={emailError || 'Please enter your email address to continue'}
                     placeholder="your.email@example.com"
                     sx={{ maxWidth: 400 }}
                   />
                 </Box>
                 
                 <Box display="flex" gap={2} justifyContent="center">
                                      <Button
                     variant="contained"
                     size="large"
                     startIcon={checkingUser ? <CircularProgress size={20} /> : <CheckCircle />}
                     onClick={handleAccept}
                     disabled={!email || !!emailError || checkingUser}
                     sx={{ minWidth: 120 }}
                   >
                     {checkingUser ? 'Checking...' : 'Yes, Continue'}
                   </Button>
                  <Button
                     variant="outlined"
                     size="large"
                     startIcon={<Cancel />}
                     onClick={handleDecline}
                     disabled={checkingUser}
                     sx={{ minWidth: 120 }}
                   >
                     No, Thanks
                   </Button>
                 </Box>
               </Box>
             </Box>
           </CardContent>
         </Card>
       </Box>
     </Container>
   );
};

export default InviteLanding;
